// GDBS Adv Module - Multi-species MD, Coupled Workflows, Batch Sweeps
import { api, getToken } from './api.js';
import { runMD, runFem } from './hpc-engines.js';
import { initRfCompaction } from './rf-compaction.js';
import { initPhysicsCalc } from './physics-calc.js';
import { initEpr, eprAdvMarkup } from './hpc-epr.js';
import { initWaveguide, waveguideAdvMarkup } from './hpc-waveguide.js';
import { coexistenceMarkup, initCoexistence } from './coexistence-ui.js';

// Inject the EPR (Pro) module as an Adv tab. Appended after the base view is
// built so it joins the existing adv-tab-btn / adv-tab-content system; the
// generic switch handler is bound at setup, so this button gets its own.
function injectEprTab() {
  const tabs = document.querySelector('#view-adv .adv-tabs');
  const content = document.querySelector('#view-adv .panel-content');
  if (!tabs || !content || document.getElementById('adv-tab-epr')) return;
  const btn = document.createElement('button');
  btn.className = 'adv-tab-btn';
  btn.dataset.tab = 'epr';
  btn.innerHTML = '<i class="fas fa-magnet"></i> EPR';
  const jobsBtn = document.getElementById('adv-jobs-btn');
  if (jobsBtn) tabs.insertBefore(btn, jobsBtn); else tabs.appendChild(btn);
  const wrap = document.createElement('div');
  wrap.id = 'adv-tab-epr';
  wrap.className = 'adv-tab-content';
  wrap.innerHTML = eprAdvMarkup();
  content.appendChild(wrap);
  btn.addEventListener('click', () => {
    document.querySelectorAll('#view-adv .adv-tab-btn').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('#view-adv .adv-tab-content').forEach(c => c.classList.remove('active'));
    btn.classList.add('active');
    wrap.classList.add('active');
  });
  initEpr();
}

// Inject the Nonlinear Waveguide Array (Pro) module as an Adv tab.
function injectWaveguideTab() {
  const tabs = document.querySelector('#view-adv .adv-tabs');
  const content = document.querySelector('#view-adv .panel-content');
  if (!tabs || !content || document.getElementById('adv-tab-waveguide')) return;
  const btn = document.createElement('button');
  btn.className = 'adv-tab-btn';
  btn.dataset.tab = 'waveguide';
  btn.innerHTML = '<i class="fas fa-wave-square"></i> Waveguide Array';
  const jobsBtn = document.getElementById('adv-jobs-btn');
  if (jobsBtn) tabs.insertBefore(btn, jobsBtn); else tabs.appendChild(btn);
  const wrap = document.createElement('div');
  wrap.id = 'adv-tab-waveguide';
  wrap.className = 'adv-tab-content';
  wrap.innerHTML = waveguideAdvMarkup();
  content.appendChild(wrap);
  btn.addEventListener('click', () => {
    document.querySelectorAll('#view-adv .adv-tab-btn').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('#view-adv .adv-tab-content').forEach(c => c.classList.remove('active'));
    btn.classList.add('active');
    wrap.classList.add('active');
  });
  initWaveguide();
}

// Inject the RF Coexistence & Authorization (Spectrum Strike B+C) module as an Adv tab.
function injectCoexistenceTab() {
  const tabs = document.querySelector('#view-adv .adv-tabs');
  const content = document.querySelector('#view-adv .panel-content');
  if (!tabs || !content || document.getElementById('adv-tab-coexistence')) return;
  const btn = document.createElement('button');
  btn.className = 'adv-tab-btn';
  btn.dataset.tab = 'coexistence';
  btn.innerHTML = '<i class="fas fa-tower-broadcast"></i> Coexistence';
  const jobsBtn = document.getElementById('adv-jobs-btn');
  if (jobsBtn) tabs.insertBefore(btn, jobsBtn); else tabs.appendChild(btn);
  const wrap = document.createElement('div');
  wrap.id = 'adv-tab-coexistence';
  wrap.className = 'adv-tab-content';
  wrap.innerHTML = coexistenceMarkup();
  content.appendChild(wrap);
  btn.addEventListener('click', () => {
    document.querySelectorAll('#view-adv .adv-tab-btn').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('#view-adv .adv-tab-content').forEach(c => c.classList.remove('active'));
    btn.classList.add('active');
    wrap.classList.add('active');
  });
  initCoexistence();
}

export function init() {
  setupAdvModule();
}

function setupAdvModule() {
  const container = document.getElementById('view-adv');
  if (!container) return;

  container.innerHTML = `
    <div class="panel">
      <div class="panel-header">
        <h2><i class="fas fa-layer-group"></i> Advanced Multi-Physics Module</h2>
        <p class="panel-subtitle">Multi-species MD • Coupled Workflows • Batch Parameter Sweeps</p>
      </div>
      <div class="panel-content">
        <div class="adv-tabs">
          <button class="adv-tab-btn active" data-tab="multi-species">Multi-Species MD</button>
          <button class="adv-tab-btn" data-tab="coupled">Coupled MD↔FEM</button>
          <button class="adv-tab-btn" data-tab="batch">Batch Sweeps</button>
          <button class="adv-tab-btn" data-tab="rf-pro">RF Pro</button>
          <button class="adv-tab-btn" data-tab="calc">Calc</button>
          <button class="adv-tab-btn" id="adv-jobs-btn"><i class="fas fa-list"></i> Jobs</button>
        </div>

        <!-- Multi-Species MD Tab -->
        <div id="adv-tab-multi-species" class="adv-tab-content active">
          <div style="margin-bottom: 1rem;">
            <h3 style="margin: 0;">Multi-Species Molecular Dynamics</h3>
            <p style="margin: 0.5rem 0 0 0;">Simulate alloys with different atomic species and interaction potentials.</p>
          </div>

          <div class="form-grid">
            <div class="field">
              <label>Species A Fraction</label>
              <input type="range" id="adv-species-a-frac" min="0" max="1" step="0.1" value="0.6">
              <span id="adv-species-a-frac-val">0.6</span>
            </div>
            <div class="field">
              <label>Species A Epsilon (eV)</label>
              <input type="number" id="adv-species-a-eps" step="0.1" value="1.0">
            </div>
            <div class="field">
              <label>Species A Sigma (Å)</label>
              <input type="number" id="adv-species-a-sig" step="0.1" value="3.4">
            </div>
            <div class="field">
              <label>Species B Epsilon (eV)</label>
              <input type="number" id="adv-species-b-eps" step="0.1" value="2.0">
            </div>
            <div class="field">
              <label>Species B Sigma (Å)</label>
              <input type="number" id="adv-species-b-sig" step="0.1" value="3.8">
            </div>
            <div class="field">
              <label>Total Atoms</label>
              <input type="number" id="adv-atoms" value="10000">
            </div>
            <div class="field">
              <label>Simulation Steps</label>
              <input type="number" id="adv-steps" value="5000">
            </div>
            <div class="field">
              <label>GeoNum Zone</label>
              <select id="adv-zone">
                <option value="materials">Materials (lattice-symmetric)</option>
                <option value="fluids">Fluids (uniform grid)</option>
                <option value="theory">Theory (Lucas zones)</option>
              </select>
            </div>
            <div class="field">
              <label>GeoNum Shades</label>
              <select id="adv-shades">
                <option value="512">512 (fast)</option>
                <option value="1024">1024</option>
                <option value="2048" selected>2048 (balanced)</option>
                <option value="4096">4096 (max precision)</option>
              </select>
            </div>
          </div>

          <button class="btn btn-primary" id="adv-run-md">Run Multi-Species MD</button>
          <div id="adv-md-result" class="result-area"></div>
        </div>

        <!-- Coupled Workflow Tab -->
        <div id="adv-tab-coupled" class="adv-tab-content">
          <div style="margin-bottom: 1rem;">
            <h3 style="margin: 0;">Coupled MD ↔ FEM Workflow</h3>
            <p style="margin: 0.5rem 0 0 0;">Simulate surface erosion → thermal stress feedback loops.</p>
          </div>
          <div class="form-grid">
            <div class="field">
              <label>Max Cycles</label>
              <input type="number" id="adv-coupled-cycles" value="10">
            </div>
            <div class="field">
              <label>Convergence Tolerance (Å)</label>
              <input type="number" id="adv-coupled-tol" step="0.01" value="0.01">
            </div>
          </div>
          <button class="btn btn-primary" id="adv-run-coupled">Run Coupled Workflow</button>
          <div id="adv-coupled-result" class="result-area"></div>
        </div>

        <!-- Batch Sweeps Tab -->
        <div id="adv-tab-batch" class="adv-tab-content">
          <div style="margin-bottom: 1rem;">
            <h3 style="margin: 0;">Batch Parameter Sweeps</h3>
            <p style="margin: 0.5rem 0 0 0;">Screen multiple configurations in parallel.</p>
          </div>
          <button class="btn btn-primary" id="adv-run-batch">Submit Batch Sweep</button>
          <div id="adv-batch-result" class="result-area"></div>
        </div>

        <!-- RF Pro Tab -->
        <div id="adv-tab-rf-pro" class="adv-tab-content">
          <div style="margin-bottom: 1.25rem;">
            <h3 style="margin: 0;">RF Pro — constellation shaping</h3>
            <p style="margin: 0.5rem 0 0 0; color: var(--text-dim);">
              Min-distance-optimal APSK + soft-LLR weighting — real PA-limited link margin (~1-1.5 dB peak + ≤1.53 dB shaping), at or below the Shannon bound (it does NOT exceed it).
            </p>
          </div>

          <div class="form-grid">
            <div class="field">
              <label>Modulation</label>
              <select id="rf-modulation">
                <option value="QPSK" selected>QPSK</option>
                <option value="BPSK">BPSK</option>
                <option value="QAM16">16-QAM</option>
                <option value="QAM64">64-QAM</option>
                <option value="QAM256">256-QAM</option>
                <option value="OFDM">OFDM (×64 sub-carriers)</option>
              </select>
            </div>
            <div class="field">
              <label>Symbols</label>
              <input type="number" id="rf-n-symbols" value="512" min="64" max="4096">
            </div>
            <div class="field">
              <label>SNR (dB)</label>
              <input type="number" id="rf-snr-db" value="15" step="1">
            </div>
            <div class="field">
              <label>Carrier (MHz)</label>
              <input type="number" id="rf-carrier-mhz" value="2400" step="100">
            </div>
            <div class="field">
              <label>Bandwidth (MHz)</label>
              <input type="number" id="rf-bw-mhz" value="20" step="1">
            </div>
            <div class="field">
              <label>Möbius Threshold</label>
              <input type="number" id="rf-geo-threshold" value="0.5" step="0.05" min="0.05" max="3.0">
            </div>
          </div>

          <div style="display: flex; gap: 0.75rem; flex-wrap: wrap; align-items: center; margin-bottom: 1rem;">
            <button class="btn btn-primary" id="rf-run-pipeline">Run Analysis</button>
            <span id="rf-status" style="color: var(--text-dim); font-size: 0.875rem;"></span>
          </div>

          <!-- Pipeline charts -->
          <div id="rf-pipeline-section" style="display:none;">
            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem;">
              <div>
                <p style="margin:0 0 0.25rem; font-size:0.8rem; color:var(--text-dim);">IQ Constellation</p>
                <div style="height:280px; position:relative;">
                  <canvas id="rf-constellation-chart"></canvas>
                </div>
              </div>
              <div>
                <p style="margin:0 0 0.25rem; font-size:0.8rem; color:var(--text-dim);">Poincaré Disk</p>
                <div style="height:280px; position:relative;">
                  <canvas id="rf-disk-chart"></canvas>
                </div>
              </div>
            </div>
            <div id="rf-pipeline-metrics" style="
              background: var(--bg-alt);
              border: 1px solid var(--border);
              border-radius: 8px;
              padding: 1rem 1.25rem;
              margin-bottom: 1rem;
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
              gap: 0.75rem;
            "></div>
          </div>

          <!-- Sweep charts -->
          <div id="rf-sweep-section" style="display:none;">
            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem;">
              <div>
                <p style="margin:0 0 0.25rem; font-size:0.8rem; color:var(--text-dim);">BER vs SNR</p>
                <div style="height:280px; position:relative;">
                  <canvas id="rf-ber-chart"></canvas>
                </div>
              </div>
              <div>
                <p style="margin:0 0 0.25rem; font-size:0.8rem; color:var(--text-dim);">Capacity vs SNR (bits/s/Hz)</p>
                <div style="height:280px; position:relative;">
                  <canvas id="rf-cap-chart"></canvas>
                </div>
              </div>
            </div>
          </div>

          <!-- Prediction -->
          <div id="rf-prediction" style="display:none;
            background: var(--bg-alt);
            border: 1px solid var(--border);
            border-left: 3px solid #34d399;
            border-radius: 8px;
            padding: 1.25rem;
            margin-bottom: 1rem;
          "></div>

          <!-- Proof Board -->
          <div id="rf-proof-board" style="display:none;
            background: var(--bg-alt);
            border: 1px solid var(--border);
            border-left: 3px solid #a78bfa;
            border-radius: 8px;
            padding: 1.25rem;
            margin-bottom: 1rem;
          "></div>

          <!-- Methodology -->
          <div id="rf-methodology" style="display:none;
            background: var(--bg-alt);
            border: 1px solid var(--border);
            border-left: 3px solid #06b6d4;
            border-radius: 8px;
            padding: 1.25rem;
            font-size: 0.85rem;
            color: var(--text-dim);
          ">
            <h4 style="margin: 0 0 0.75rem; color: var(--text-bright); font-size: 0.95rem;">Methodology</h4>
            <div id="rf-methodology-body"></div>
          </div>
        </div>
      </div>
    </div>

        <!-- Calc Tab -->
        <div id="adv-tab-calc" class="adv-tab-content">
          <div id="phys-calc-root"></div>
        </div>

    <!-- Jobs Viewer Modal -->
    <div id="adv-jobs-modal" class="modal hidden" style="display: none;">
      <div class="modal-content" style="max-width: 1000px; max-height: 85vh; display: flex; flex-direction: column; overflow: hidden;">
        <div class="modal-header" style="padding: 1.5rem 2rem; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center; flex-shrink: 0;">
          <h3 id="adv-jobs-modal-title" style="margin: 0; font-size: 18px; font-weight: 600; color: var(--text-bright);">
            <i class="fas fa-list-alt"></i> Jobs Dashboard
          </h3>
          <button class="modal-close" id="adv-jobs-modal-close" style="font-size: 28px; color: var(--text-dim); background: none; border: none; cursor: pointer; padding: 0; line-height: 1; transition: color 0.2s;" onmouseover="this.style.color='var(--text-bright)';" onmouseout="this.style.color='var(--text-dim)';">&times;</button>
        </div>
        <div style="flex: 1; overflow-y: auto; display: flex; flex-direction: column;">
          <div class="adv-jobs-tabs" style="display: flex; gap: 0; border-bottom: 1px solid var(--border); background: var(--bg-alt); padding: 0 2rem; flex-shrink: 0;">
            <button class="adv-jobs-tab active" data-status="running" style="padding: 1rem 1.5rem; border: none; background: rgba(245, 158, 11, 0.1); border-bottom: 3px solid #f59e0b; cursor: pointer; position: relative; color: var(--text-bright); font-weight: 500; font-size: 14px; transition: all 0.2s;">
              <i class="fas fa-spinner"></i> Running <span class="job-count" id="adv-jobs-count-running" style="display: inline-block; min-width: 20px; padding: 2px 8px; background: rgba(255,152,0,0.2); border-radius: 12px; font-size: 12px; margin-left: 6px;">0</span>
            </button>
            <button class="adv-jobs-tab" data-status="completed" style="padding: 1rem 1.5rem; border: none; background: none; cursor: pointer; position: relative; color: var(--text-dim); font-weight: 500; font-size: 14px; transition: all 0.2s;">
              <i class="fas fa-check-circle"></i> Completed <span class="job-count" id="adv-jobs-count-completed" style="display: inline-block; min-width: 20px; padding: 2px 8px; background: rgba(76,175,80,0.2); border-radius: 12px; font-size: 12px; margin-left: 6px;">0</span>
            </button>
            <button class="adv-jobs-tab" data-status="failed" style="padding: 1rem 1.5rem; border: none; background: none; cursor: pointer; position: relative; color: var(--text-dim); font-weight: 500; font-size: 14px; transition: all 0.2s;">
              <i class="fas fa-exclamation-circle"></i> Failed <span class="job-count" id="adv-jobs-count-failed" style="display: inline-block; min-width: 20px; padding: 2px 8px; background: rgba(244,67,54,0.2); border-radius: 12px; font-size: 12px; margin-left: 6px;">0</span>
            </button>
          </div>
          <div id="adv-jobs-list" style="min-height: 300px; padding: 1.5rem 2rem; flex: 1; overflow-y: auto;"></div>
        </div>
      </div>
    </div>
  `;

  // Exclude the Jobs button — it opens a modal, not a tab panel.
  document.querySelectorAll('.adv-tab-btn').forEach(btn => {
    if (!btn.id || !btn.id.includes('jobs')) {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.adv-tab-btn').forEach(b => b.classList.remove('active'));
        document.querySelectorAll('.adv-tab-content').forEach(c => c.classList.remove('active'));
        btn.classList.add('active');
        document.getElementById(`adv-tab-${btn.dataset.tab}`).classList.add('active');
      });
    }
  });

  injectCoexistenceTab();
  injectEprTab();
  injectWaveguideTab();

  const fracSlider = document.getElementById('adv-species-a-frac');
  const fracVal = document.getElementById('adv-species-a-frac-val');
  fracSlider.addEventListener('input', () => {
    fracVal.textContent = fracSlider.value;
  });

  document.getElementById('adv-run-md').addEventListener('click', async () => {
    const fracA = parseFloat(document.getElementById('adv-species-a-frac').value);
    const config = {
      species: [
        {
          tag: 'A',
          fraction: fracA,
          epsilon: parseFloat(document.getElementById('adv-species-a-eps').value),
          sigma: parseFloat(document.getElementById('adv-species-a-sig').value),
          mass: 58.69
        },
        {
          tag: 'B',
          fraction: 1 - fracA,
          epsilon: parseFloat(document.getElementById('adv-species-b-eps').value),
          sigma: parseFloat(document.getElementById('adv-species-b-sig').value),
          mass: 26.98
        }
      ],
      atoms: parseInt(document.getElementById('adv-atoms').value),
      steps: parseInt(document.getElementById('adv-steps').value),
      dt: 0.001,
      targetT: 2.0,
      density: 0.95,
      precision: {
        zone: document.getElementById('adv-zone').value,
        shades: parseInt(document.getElementById('adv-shades').value),
        alertThreshold: 1.0
      }
    };

    const resultDiv = document.getElementById('adv-md-result');
    resultDiv.innerHTML = '<p>Submitting job...</p>';

    try {
      const result = await api.advMultiSpeciesMD(config);
      if (result.error || !result.job_id) {
        resultDiv.innerHTML = `<div class="error">Error: ${result.error || 'Please log in to use this feature'}</div>`;
        return;
      }
      const jobId = result.job_id;
      resultDiv.innerHTML = `<p>Running simulation (Job ${jobId.substring(0,8)}...)...</p>`;
      try {
        // Client-side run uses species A as the primary potential (single-species engine).
        const sp = config.species[0];
        const mdResults = runMD({
          n_atoms: config.atoms,
          n_steps: config.steps,
          dt: config.dt,
          target_temp: config.targetT,
          density: config.density,
          epsilon_ev: sp.epsilon,
          sigma_a: sp.sigma,
          mass_amu: sp.mass
        });
        mdResults.species = config.species;
        await api.advCompleteJob(jobId, mdResults);
        resultDiv.innerHTML = `
          <div class="success">
            <p><strong>Job Complete!</strong></p>
            <p>Job ID: ${jobId}</p>
            <p>Atoms: ${mdResults.n_atoms} | Final T: ${mdResults.final_temperature.toFixed(3)} | Diffusion: ${mdResults.avg_diffusion.toFixed(4)}</p>
          </div>
        `;
      } catch (compErr) {
        await api.advFailJob(jobId, { message: compErr.message || 'Computation failed' });
        resultDiv.innerHTML = `<div class="error">Computation failed: ${compErr.message}</div>`;
      }
    } catch (error) {
      resultDiv.innerHTML = `<div class="error">Error: ${error.message || 'Request failed'}</div>`;
    }
  });

  document.getElementById('adv-run-coupled').addEventListener('click', async () => {
    const resultDiv = document.getElementById('adv-coupled-result');
    resultDiv.innerHTML = '<p>Submitting coupled workflow...</p>';

    try {
      const config = {
        mdConfig: {
          species: [{ tag: 'Fe', fraction: 1.0, epsilon: 0.4, sigma: 2.5, mass: 55.85 }],
          atoms: 8000,
          steps: 3000,
          dt: 0.001,
          targetT: 2.0,
          density: 0.90,
          precision: { zone: 'materials', shades: 2048, alertThreshold: 1.0 }
        },
        femConfig: { gridNx: 128, gridNy: 128, topBC: 2000, bottomBC: 400 },
        maxCycles: parseInt(document.getElementById('adv-coupled-cycles').value),
        convergenceTol: parseFloat(document.getElementById('adv-coupled-tol').value)
      };

      const result = await api.advCoupledWorkflow(config);
      if (result.error || !result.job_id) {
        resultDiv.innerHTML = `<div class="error">Error: ${result.error || 'Please log in to use this feature'}</div>`;
        return;
      }
      const jobId = result.job_id;
      resultDiv.innerHTML = `<p>Running coupled MD↔FEM (Job ${jobId.substring(0,8)}...)...</p>`;
      try {
        const sp = config.mdConfig.species[0];
        const mdResults = runMD({
          n_atoms: config.mdConfig.atoms,
          n_steps: config.mdConfig.steps,
          dt: config.mdConfig.dt,
          target_temp: config.mdConfig.targetT,
          density: config.mdConfig.density,
          epsilon_ev: sp.epsilon,
          sigma_a: sp.sigma,
          mass_amu: sp.mass
        });
        const femResults = runFem({
          nx: config.femConfig.gridNx,
          ny: config.femConfig.gridNy,
          bc_top: config.femConfig.topBC,
          bc_bottom: config.femConfig.bottomBC
        });
        const coupledResults = { md: mdResults, fem: femResults, cycles: config.maxCycles };
        await api.advCompleteJob(jobId, coupledResults);
        resultDiv.innerHTML = `
          <div class="success">
            <p><strong>Workflow Complete!</strong></p>
            <p>Job ID: ${jobId}</p>
            <p>MD T: ${mdResults.final_temperature.toFixed(3)} | FEM converged: ${femResults.converged} (${femResults.iterations} iter)</p>
          </div>
        `;
      } catch (compErr) {
        await api.advFailJob(jobId, { message: compErr.message || 'Computation failed' });
        resultDiv.innerHTML = `<div class="error">Computation failed: ${compErr.message}</div>`;
      }
    } catch (error) {
      resultDiv.innerHTML = `<div class="error">Error: ${error.message || 'Request failed'}</div>`;
    }
  });

  document.getElementById('adv-run-batch').addEventListener('click', async () => {
    const resultDiv = document.getElementById('adv-batch-result');
    resultDiv.innerHTML = '<p>Submitting batch sweep...</p>';

    try {
      const config = {
        baseConfig: {
          species: [
            { tag: 'A', fraction: 0.5, epsilon: 1.0, sigma: 3.4, mass: 58.69 },
            { tag: 'B', fraction: 0.5, epsilon: 2.0, sigma: 3.8, mass: 26.98 }
          ],
          atoms: 5000,
          steps: 2000,
          dt: 0.001,
          targetT: 2.0,
          density: 0.95,
          precision: { zone: 'materials', shades: 2048, alertThreshold: 1.0 }
        },
        sweepVars: {
          'species[0].epsilon': [0.8, 1.0, 1.2],
          'species[1].epsilon': [1.5, 2.0, 2.5]
        },
        metrics: ['erosion_rate_A', 'symlink_count', 'max_drift']
      };

      const result = await api.advBatchSweep(config);
      if (result.error || !result.batch_id) {
        resultDiv.innerHTML = `<div class="error">Error: ${result.error || 'Please log in to use this feature'}</div>`;
        return;
      }
      const jobId = result.batch_id;
      resultDiv.innerHTML = `<p>Running batch sweep (${result.total_runs} runs, Job ${jobId.substring(0,8)}...)...</p>`;
      try {
        const sweepEntries = Object.entries(config.sweepVars);
        const runResults = [];
        const sp0 = config.baseConfig.species[0];
        const epsValues = config.sweepVars['species[0].epsilon'] || [sp0.epsilon];
        const eps1Values = config.sweepVars['species[1].epsilon'] || [config.baseConfig.species[1]?.epsilon || 2.0];
        for (const eps0 of epsValues) {
          for (const eps1 of eps1Values) {
            const r = runMD({
              n_atoms: config.baseConfig.atoms,
              n_steps: config.baseConfig.steps,
              dt: config.baseConfig.dt,
              target_temp: config.baseConfig.targetT,
              density: config.baseConfig.density,
              epsilon_ev: eps0,
              sigma_a: sp0.sigma,
              mass_amu: sp0.mass
            });
            runResults.push({ 'species[0].epsilon': eps0, 'species[1].epsilon': eps1, final_temperature: r.final_temperature, avg_diffusion: r.avg_diffusion });
          }
        }
        await api.advCompleteJob(jobId, { runs: runResults, total: runResults.length });
        resultDiv.innerHTML = `
          <div class="success">
            <p><strong>Batch Complete!</strong></p>
            <p>Batch ID: ${jobId}</p>
            <p>Completed ${runResults.length} / ${result.total_runs} runs</p>
          </div>
        `;
      } catch (compErr) {
        await api.advFailJob(jobId, { message: compErr.message || 'Computation failed' });
        resultDiv.innerHTML = `<div class="error">Computation failed: ${compErr.message}</div>`;
      }
    } catch (error) {
      resultDiv.innerHTML = `<div class="error">Error: ${error.message || 'Request failed'}</div>`;
    }
  });

  let currentJobType = null;
  let currentJobStatus = 'running';

  async function loadJobs(type, status) {
    const jobs = await api.advGetJobs(type, status || undefined);
    return Array.isArray(jobs) ? jobs : [];
  }

  async function showJobsModal(type, title) {
    currentJobType = type;
    document.getElementById('adv-jobs-modal-title').textContent = `${title} - Jobs`;
    const modal = document.getElementById('adv-jobs-modal');
    modal.classList.remove('hidden');
    modal.style.display = 'flex';
    await refreshJobsList();
  }

  async function refreshJobsList() {
    const jobs = await loadJobs(currentJobType, currentJobStatus);
    const listEl = document.getElementById('adv-jobs-list');

    const allJobs = await loadJobs(currentJobType, undefined);
    document.getElementById('adv-jobs-count-running').textContent =
      allJobs.filter(j => j.status === 'running').length;
    document.getElementById('adv-jobs-count-completed').textContent =
      allJobs.filter(j => j.status === 'completed').length;
    document.getElementById('adv-jobs-count-failed').textContent =
      allJobs.filter(j => j.status === 'failed').length;

    if (jobs.length === 0) {
      listEl.innerHTML = `
        <div style="text-align: center; padding: 4rem 2rem; color: var(--text-dim);">
          <i class="fas fa-inbox" style="font-size: 48px; opacity: 0.3; margin-bottom: 1rem;"></i>
          <p style="font-size: 16px; margin: 0;">No ${currentJobStatus} jobs found</p>
          <p style="font-size: 14px; margin-top: 0.5rem; opacity: 0.7;">Jobs will appear here once submitted</p>
        </div>`;
      return;
    }

    listEl.innerHTML = jobs.map(job => {
      const statusColors = {
        completed: { bg: 'rgba(76, 175, 80, 0.15)', text: '#4CAF50', icon: 'check-circle' },
        failed: { bg: 'rgba(244, 67, 54, 0.15)', text: '#f44336', icon: 'times-circle' },
        running: { bg: 'rgba(255, 152, 0, 0.15)', text: '#FF9800', icon: 'spinner fa-spin' }
      };
      const color = statusColors[job.status] || statusColors.running;
      const duration = job.completed_at ?
        Math.round((new Date(job.completed_at) - new Date(job.created_at)) / 1000) : null;

      const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      };

      const createdDate = new Date(job.created_at);
      const estimatedMs = job.estimated_time_ms || 0;
      const projectedCompletion = new Date(createdDate.getTime() + estimatedMs);

      return `
      <div class="job-item" style="
        background: var(--bg-panel);
        border: 1px solid var(--border);
        border-left: 3px solid ${color.text};
        padding: 1.25rem 1.5rem;
        margin-bottom: 0.75rem;
        border-radius: 6px;
        transition: all 0.2s;
      " data-job-id="${job.job_id}">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 1rem;">
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem;">
              <i class="fas fa-${color.icon}" style="color: ${color.text}; font-size: 16px;"></i>
              <div style="font-weight: 600; font-size: 15px; color: var(--text-bright); font-family: 'Courier New', monospace;">
                ${job.job_id.substring(0, 12)}...
              </div>
            </div>
            <div style="font-size: 13px; color: var(--text-dim); line-height: 1.8; font-family: monospace;">
              <div style="margin-bottom: 3px;">
                <span style="display: inline-block; width: 120px; opacity: 0.8;">Created:</span>
                <span style="color: var(--text);">${formatDate(job.created_at)}</span>
              </div>
              ${job.status === 'running' && estimatedMs > 0 ? `
              <div style="margin-bottom: 3px;">
                <span style="display: inline-block; width: 120px; opacity: 0.8;">Proj Complete:</span>
                <span style="color: var(--text);">${formatDate(projectedCompletion)}</span>
              </div>` : ''}
              ${job.completed_at ? `
              <div style="margin-bottom: 3px;">
                <span style="display: inline-block; width: 120px; opacity: 0.8;">Completed:</span>
                <span style="color: var(--text);">${formatDate(job.completed_at)}</span>
                ${duration !== null ? `<span style="opacity: 0.7; margin-left: 8px;">(${duration}s)</span>` : ''}
              </div>` : ''}
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0;">
            <span style="
              padding: 6px 14px;
              background: ${color.bg};
              color: ${color.text};
              border-radius: 16px;
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              white-space: nowrap;
            ">${job.status}</span>
            ${job.status === 'completed' && job.results ? `
            <button class="job-results-btn" data-job-id="${job.job_id}" title="Open full results" style="
              width: 32px; height: 32px;
              border-radius: 50%;
              border: 1px solid rgba(79,195,247,0.4);
              background: rgba(79,195,247,0.1);
              color: #4fc3f7;
              cursor: pointer;
              font-size: 15px;
              display: flex; align-items: center; justify-content: center;
              transition: all 0.2s; flex-shrink: 0;
            " onmouseover="this.style.background='rgba(79,195,247,0.25)'" onmouseout="this.style.background='rgba(79,195,247,0.1)'">
              <i class="fas fa-chart-bar"></i>
            </button>` : ''}
            <button class="job-details-btn" data-job-id="${job.job_id}" title="Job details" style="
              width: 32px; height: 32px;
              border-radius: 50%;
              border: 1px solid var(--border);
              background: var(--bg-alt);
              color: var(--text-dim);
              cursor: pointer;
              font-size: 15px;
              display: flex; align-items: center; justify-content: center;
              transition: all 0.2s; flex-shrink: 0;
            " onmouseover="this.style.color='var(--text-bright)'; this.style.borderColor='var(--text-dim)'" onmouseout="this.style.color='var(--text-dim)'; this.style.borderColor='var(--border)'">
              <i class="fas fa-exclamation-circle"></i>
            </button>
          </div>
        </div>
      </div>`;
    }).join('');

    listEl.querySelectorAll('.job-details-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        await showJobDetails(btn.dataset.jobId);
      });
    });

    listEl.querySelectorAll('.job-results-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const job = await api.advGetJob(btn.dataset.jobId);
        if (job.results) {
          const html = buildResultsPage(job);
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
      });
    });
  }

  async function showJobDetails(jobId) {
    const job = await api.advGetJob(jobId);
    const statusColors = {
      completed: { bg: 'rgba(76, 175, 80, 0.15)', text: '#4CAF50', icon: 'check-circle' },
      failed: { bg: 'rgba(244, 67, 54, 0.15)', text: '#f44336', icon: 'times-circle' },
      running: { bg: 'rgba(255, 152, 0, 0.15)', text: '#FF9800', icon: 'spinner fa-spin' }
    };
    const color = statusColors[job.status] || statusColors.running;
    const duration = job.completed_at ?
      Math.round((new Date(job.completed_at) - new Date(job.created_at)) / 1000) : null;

    const listEl = document.getElementById('adv-jobs-list');
    listEl.innerHTML = `
      <button id="adv-jobs-back" style="
        margin-bottom: 1.5rem;
        padding: 0.75rem 1.25rem;
        background: var(--bg-panel);
        border: 1px solid var(--border);
        border-radius: 6px;
        cursor: pointer;
        color: var(--text);
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
      " onmouseover="this.style.background='var(--bg-hover)';" onmouseout="this.style.background='var(--bg-panel)';">
        <i class="fas fa-arrow-left"></i> Back to List
      </button>

      <div style="background: var(--bg-panel); border: 1px solid var(--border); border-radius: 8px; overflow: hidden;">
        <!-- Header -->
        <div style="padding: 1.5rem 2rem; border-bottom: 1px solid var(--border); background: var(--bg-alt);">
          <div style="display: flex; justify-content: space-between; align-items: start; gap: 1rem;">
            <div>
              <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem;">
                <i class="fas fa-${color.icon}" style="color: ${color.text}; font-size: 20px;"></i>
                <h4 style="margin: 0; font-size: 16px; font-weight: 600; color: var(--text-bright);">Job Details</h4>
              </div>
              <div style="font-family: 'Courier New', monospace; font-size: 13px; color: var(--text-dim); margin-left: 28px;">
                ${job.job_id}
              </div>
            </div>
            <span style="
              padding: 8px 16px;
              background: ${color.bg};
              color: ${color.text};
              border-radius: 20px;
              font-size: 13px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            ">
              <i class="fas fa-${color.icon}"></i> ${job.status}
            </span>
          </div>
        </div>

        <!-- Metadata -->
        <div style="padding: 2rem;">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin-bottom: 2rem;">
            <div style="background: var(--bg-alt); padding: 1rem 1.25rem; border-radius: 6px; border-left: 3px solid var(--text-dim);">
              <div style="font-size: 12px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.5rem;">Created</div>
              <div style="font-size: 14px; color: var(--text-bright); font-weight: 500;">
                ${new Date(job.created_at).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
            ${job.completed_at ? `
            <div style="background: var(--bg-alt); padding: 1rem 1.25rem; border-radius: 6px; border-left: 3px solid ${color.text};">
              <div style="font-size: 12px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.5rem;">Completed</div>
              <div style="font-size: 14px; color: var(--text-bright); font-weight: 500;">
                ${new Date(job.completed_at).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
            <div style="background: var(--bg-alt); padding: 1rem 1.25rem; border-radius: 6px; border-left: 3px solid var(--text-dim);">
              <div style="font-size: 12px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.5rem;">Duration</div>
              <div style="font-size: 14px; color: var(--text-bright); font-weight: 500;">
                ${duration !== null ? `${duration}s` : 'N/A'}
              </div>
            </div>
            ` : ''}
          </div>

          ${job.config ? `
          <div style="margin-bottom: 1.5rem;">
            <h5 style="font-size: 13px; color: var(--text-bright); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem; font-weight: 600;">
              <i class="fas fa-cog"></i> Configuration
            </h5>
            <pre style="
              background: var(--bg-alt);
              border: 1px solid var(--border);
              padding: 1.25rem;
              border-radius: 6px;
              overflow-x: auto;
              font-size: 12px;
              line-height: 1.6;
              color: var(--text);
              margin: 0;
            ">${JSON.stringify(job.config, null, 2)}</pre>
          </div>
          ` : ''}

          ${job.results ? `
          <div id="adv-job-results-viz" style="margin-bottom: 1.5rem;">
            <h5 style="font-size: 13px; color: var(--text-bright); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem; font-weight: 600;">
              <i class="fas fa-chart-line"></i> Results
            </h5>
            <div id="adv-results-content"></div>
          </div>
          ` : ''}

          ${job.error ? `
          <div style="margin-bottom: 1.5rem;">
            <h5 style="font-size: 13px; color: #f44336; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem; font-weight: 600;">
              <i class="fas fa-exclamation-triangle"></i> Error Details
            </h5>
            <pre style="
              background: rgba(244, 67, 54, 0.1);
              border: 1px solid rgba(244, 67, 54, 0.3);
              padding: 1.25rem;
              border-radius: 6px;
              overflow-x: auto;
              font-size: 12px;
              line-height: 1.6;
              color: #f44336;
              margin: 0;
            ">${JSON.stringify(job.error, null, 2)}</pre>
          </div>
          ` : ''}

          <div style="display: flex; gap: 1rem; padding-top: 1rem; border-top: 1px solid var(--border);">
            ${job.results ? `
            <button id="adv-open-results" style="
              padding: 0.75rem 1.5rem;
              background: rgba(79, 195, 247, 0.15);
              border: 1px solid rgba(79, 195, 247, 0.3);
              border-radius: 6px;
              cursor: pointer;
              color: #4fc3f7;
              font-size: 14px;
              font-weight: 500;
              transition: all 0.2s;
            " onmouseover="this.style.background='rgba(79, 195, 247, 0.25)';" onmouseout="this.style.background='rgba(79, 195, 247, 0.15)';">
              <i class="fas fa-external-link-alt"></i> Open Full Results
            </button>` : ''}
            <button id="adv-delete-job" data-job-id="${job.job_id}" style="
              padding: 0.75rem 1.5rem;
              background: rgba(244, 67, 54, 0.15);
              border: 1px solid rgba(244, 67, 54, 0.3);
              border-radius: 6px;
              cursor: pointer;
              color: #f44336;
              font-size: 14px;
              font-weight: 500;
              transition: all 0.2s;
            " onmouseover="this.style.background='rgba(244, 67, 54, 0.25)';" onmouseout="this.style.background='rgba(244, 67, 54, 0.15)';">
              <i class="fas fa-trash-alt"></i> Delete Job
            </button>
          </div>
        </div>
      </div>
    `;

    document.getElementById('adv-jobs-back').addEventListener('click', refreshJobsList);
    document.getElementById('adv-delete-job').addEventListener('click', async (e) => {
      if (confirm('Delete this job?')) {
        await api.advDeleteJob(e.target.dataset.jobId);
        await refreshJobsList();
      }
    });
    if (job.results) {
      document.getElementById('adv-open-results')?.addEventListener('click', () => {
        const html = buildResultsPage(job);
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      });
    }

    if (job.results) {
      renderJobResults(job.type, job.results, document.getElementById('adv-results-content'));
    }
  }

  function buildResultsPage(job) {
    const r = job.results;
    const type = job.type;
    const title = { multi_species_md: 'Multi-Species MD', coupled_workflow: 'Coupled MD↔FEM', batch_sweep: 'Batch Parameter Sweep' }[type] || type;

    let bodyContent = '';

    if (type === 'multi_species_md') {
      const snaps = r.snapshots || [];
      const rdf = r.rdf || [];
      const species = r.species || [];
      bodyContent = `
        <div class="stat-grid">
          <div class="stat"><div class="label">Atoms</div><div class="value">${r.n_atoms || '—'}</div></div>
          <div class="stat"><div class="label">Final Temp (LJ)</div><div class="value">${r.final_temperature != null ? r.final_temperature.toFixed(4) : '—'}</div></div>
          <div class="stat"><div class="label">Final Pressure (LJ)</div><div class="value">${r.final_pressure != null ? r.final_pressure.toFixed(4) : '—'}</div></div>
          <div class="stat"><div class="label">Diffusion Coeff</div><div class="value">${r.avg_diffusion != null ? r.avg_diffusion.toFixed(5) : '—'}</div></div>
          <div class="stat"><div class="label">Box Length (Å)</div><div class="value">${r.box_length_a != null ? r.box_length_a.toFixed(2) : '—'}</div></div>
          ${species.length ? `<div class="stat"><div class="label">Species</div><div class="value">${species.map(s => s.tag + '(' + Math.round(s.fraction * 100) + '%)').join(' / ')}</div></div>` : ''}
        </div>
        ${snaps.length ? `<div class="chart-row">
          <div class="chart-box"><div class="chart-label">Temperature vs Step</div><canvas id="c-temp"></canvas></div>
          <div class="chart-box"><div class="chart-label">Potential Energy vs Step</div><canvas id="c-pe"></canvas></div>
          <div class="chart-box"><div class="chart-label">Pressure vs Step</div><canvas id="c-pres"></canvas></div>
        </div>` : ''}
        ${rdf.length ? `<div class="chart-box full"><div class="chart-label">Radial Distribution Function g(r)</div><canvas id="c-rdf"></canvas></div>` : ''}
      `;
    } else if (type === 'coupled_workflow') {
      const md = r.md || {}, fem = r.fem || {};
      const snaps = md.snapshots || [];
      const femRow = fem.field ? fem.field[Math.floor(fem.field.length / 2)] : null;
      bodyContent = `
        <h3 class="section-title">MD Phase</h3>
        <div class="stat-grid">
          <div class="stat"><div class="label">Atoms</div><div class="value">${md.n_atoms || '—'}</div></div>
          <div class="stat"><div class="label">Final Temp (LJ)</div><div class="value">${md.final_temperature != null ? md.final_temperature.toFixed(4) : '—'}</div></div>
          <div class="stat"><div class="label">Diffusion</div><div class="value">${md.avg_diffusion != null ? md.avg_diffusion.toFixed(5) : '—'}</div></div>
        </div>
        <h3 class="section-title">FEM Phase</h3>
        <div class="stat-grid">
          <div class="stat"><div class="label">Converged</div><div class="value" style="color:${fem.converged ? '#4CAF50' : '#f44336'}">${fem.converged ? 'Yes' : 'No'}</div></div>
          <div class="stat"><div class="label">Iterations</div><div class="value">${fem.iterations || '—'}</div></div>
          <div class="stat"><div class="label">Max Field</div><div class="value">${fem.max_value != null ? fem.max_value.toFixed(1) : '—'}</div></div>
          <div class="stat"><div class="label">Residual</div><div class="value">${fem.residual != null ? fem.residual.toExponential(2) : '—'}</div></div>
          <div class="stat"><div class="label">Cycles</div><div class="value">${r.cycles || '—'}</div></div>
        </div>
        ${snaps.length ? `<div class="chart-row">
          <div class="chart-box"><div class="chart-label">MD Temperature vs Step</div><canvas id="c-md-temp"></canvas></div>
          <div class="chart-box"><div class="chart-label">MD Potential Energy vs Step</div><canvas id="c-md-pe"></canvas></div>
        </div>` : ''}
        ${femRow ? `<div class="chart-box full"><div class="chart-label">FEM Thermal Field — Center Row Profile</div><canvas id="c-fem-row"></canvas></div>` : ''}
      `;
    } else if (type === 'batch_sweep') {
      const runs = r.runs || [];
      const headers = runs.length ? Object.keys(runs[0]) : [];
      const tableRows = runs.map((run, i) => `<tr>${[i + 1, ...Object.values(run).map(v => typeof v === 'number' ? v.toFixed(5) : v)].map(v => `<td>${v}</td>`).join('')}</tr>`).join('');
      bodyContent = `
        <div class="stat-grid">
          <div class="stat"><div class="label">Total Runs</div><div class="value">${r.total || runs.length}</div></div>
        </div>
        <table><thead><tr><th>#</th>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table>
        <div class="chart-row">
          <div class="chart-box"><div class="chart-label">Final Temperature by Run</div><canvas id="c-b-temp"></canvas></div>
          <div class="chart-box"><div class="chart-label">Diffusion by Run</div><canvas id="c-b-diff"></canvas></div>
        </div>
      `;
    }

    const chartSetup = JSON.stringify({ type: job.type, results: r });

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>GDBS — ${title} Results</title>
  <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #0f1117; color: #c9d1d9; font-family: system-ui, sans-serif; padding: 2rem; }
    h1 { font-size: 22px; color: #e6edf3; margin-bottom: 0.25rem; }
    .meta { font-size: 13px; color: #6e7681; margin-bottom: 2rem; font-family: monospace; }
    .section-title { font-size: 13px; color: #8b949e; text-transform: uppercase; letter-spacing: 0.5px; margin: 1.5rem 0 0.75rem; }
    .stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
    .stat { background: #161b22; border: 1px solid #30363d; border-radius: 6px; padding: 1rem 1.25rem; }
    .stat .label { font-size: 11px; color: #6e7681; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
    .stat .value { font-size: 20px; font-weight: 600; color: #e6edf3; }
    .chart-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 1.5rem; margin-bottom: 1.5rem; }
    .chart-box { background: #161b22; border: 1px solid #30363d; border-radius: 6px; padding: 1.25rem; }
    .chart-box.full { margin-bottom: 1.5rem; }
    .chart-label { font-size: 12px; color: #6e7681; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem; }
    table { width: 100%; border-collapse: collapse; background: #161b22; border-radius: 6px; overflow: hidden; margin-bottom: 1.5rem; font-size: 13px; }
    th { padding: 0.6rem 1rem; text-align: left; color: #6e7681; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #30363d; }
    td { padding: 0.5rem 1rem; border-bottom: 1px solid #21262d; }
    tr:last-child td { border-bottom: none; }
  </style>
</head>
<body>
  <h1>${title} — Job Results</h1>
  <div class="meta">Job: ${job.job_id} &nbsp;|&nbsp; Completed: ${job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}</div>
  ${bodyContent}
  <script>
    const _data = ${chartSetup};
    const r = _data.results;
    const co = { responsive: true, animation: false, plugins: { legend: { display: false } },
      scales: { x: { ticks: { color: '#6e7681', maxTicksLimit: 8 }, grid: { color: 'rgba(255,255,255,0.05)' } },
                y: { ticks: { color: '#6e7681' }, grid: { color: 'rgba(255,255,255,0.05)' } } } };
    function lc(id, labels, data, color) {
      const el = document.getElementById(id); if (!el) return;
      new Chart(el, { type: 'line', data: { labels, datasets: [{ data, borderColor: color, borderWidth: 1.5, pointRadius: 0, fill: false }] }, options: co });
    }
    function bc(id, labels, data, color) {
      const el = document.getElementById(id); if (!el) return;
      new Chart(el, { type: 'bar', data: { labels, datasets: [{ data, backgroundColor: color + '80', borderColor: color, borderWidth: 1 }] }, options: co });
    }
    if (_data.type === 'multi_species_md') {
      const snaps = r.snapshots || [], rdf = r.rdf || [];
      const steps = snaps.map(s => s.step);
      lc('c-temp', steps, snaps.map(s => s.temperature), '#ff9800');
      lc('c-pe', steps, snaps.map(s => s.potential_energy), '#ce93d8');
      lc('c-pres', steps, snaps.map(s => s.pressure), '#4fc3f7');
      lc('c-rdf', rdf.map(p => p.r.toFixed(2)), rdf.map(p => p.g_r), '#4fc3f7');
    } else if (_data.type === 'coupled_workflow') {
      const md = r.md || {}, fem = r.fem || {};
      const snaps = md.snapshots || [];
      const steps = snaps.map(s => s.step);
      lc('c-md-temp', steps, snaps.map(s => s.temperature), '#ff9800');
      lc('c-md-pe', steps, snaps.map(s => s.potential_energy), '#ce93d8');
      if (fem.field) {
        const row = fem.field[Math.floor(fem.field.length / 2)];
        lc('c-fem-row', row.map((_, i) => i), row, '#4fc3f7');
      }
    } else if (_data.type === 'batch_sweep') {
      const runs = r.runs || [], labels = runs.map((_, i) => 'Run ' + (i+1));
      bc('c-b-temp', labels, runs.map(x => x.final_temperature), '#ff9800');
      bc('c-b-diff', labels, runs.map(x => x.avg_diffusion), '#80cbc4');
    }
  </script>
</body>
</html>`;
  }

  function renderJobResults(type, results, container) {
    if (!container) return;

    if (type === 'multi_species_md') {
      renderMDResults(results, container);
    } else if (type === 'coupled_workflow') {
      renderCoupledResults(results, container);
    } else if (type === 'batch_sweep') {
      renderBatchResults(results, container);
    } else {
      container.innerHTML = `<pre style="background:var(--bg-alt);border:1px solid var(--border);padding:1.25rem;border-radius:6px;font-size:12px;color:var(--text);overflow-x:auto;">${JSON.stringify(results, null, 2)}</pre>`;
    }
  }

  function statCard(label, value, color) {
    return `<div style="background:var(--bg-alt);padding:1rem 1.25rem;border-radius:6px;border-left:3px solid ${color || 'var(--text-dim)'};">
      <div style="font-size:11px;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">${label}</div>
      <div style="font-size:18px;font-weight:600;color:var(--text-bright);">${value}</div>
    </div>`;
  }

  function renderMDResults(r, container) {
    const snapshots = r.snapshots || [];
    const rdf = r.rdf || [];
    const species = r.species || [];

    const statsHtml = `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:1rem;margin-bottom:1.5rem;">
      ${statCard('Atoms', r.n_atoms || '—', '#4fc3f7')}
      ${statCard('Final Temp', r.final_temperature != null ? r.final_temperature.toFixed(4) + ' LJ' : '—', '#ff9800')}
      ${statCard('Final Pressure', r.final_pressure != null ? r.final_pressure.toFixed(4) + ' LJ' : '—', '#ce93d8')}
      ${statCard('Diffusion Coeff', r.avg_diffusion != null ? r.avg_diffusion.toFixed(5) : '—', '#80cbc4')}
      ${statCard('Box Length', r.box_length_a != null ? r.box_length_a.toFixed(2) + ' Å' : '—', '#a5d6a7')}
      ${species.length ? statCard('Species', species.map(s => `${s.tag}(${Math.round(s.fraction*100)}%)`).join(' / '), '#f48fb1') : ''}
    </div>`;

    let chartsHtml = '';
    if (snapshots.length > 0) {
      chartsHtml += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;margin-bottom:1.5rem;">
        <div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;">
          <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">Temperature vs Step</div>
          <canvas id="adv-chart-temp" height="160"></canvas>
        </div>
        <div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;">
          <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">Potential Energy vs Step</div>
          <canvas id="adv-chart-pe" height="160"></canvas>
        </div>
      </div>`;
    }
    if (rdf.length > 0) {
      chartsHtml += `<div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;margin-bottom:1.5rem;">
        <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">Radial Distribution Function g(r)</div>
        <canvas id="adv-chart-rdf" height="120"></canvas>
      </div>`;
    }

    container.innerHTML = statsHtml + chartsHtml;

    const chartDefaults = { responsive: true, animation: false, plugins: { legend: { display: false } }, scales: {
      x: { ticks: { color: '#888', maxTicksLimit: 6 }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.05)' } }
    }};

    if (snapshots.length > 0) {
      const steps = snapshots.map(s => s.step);
      new Chart(document.getElementById('adv-chart-temp'), {
        type: 'line', data: { labels: steps, datasets: [{ data: snapshots.map(s => s.temperature), borderColor: '#ff9800', borderWidth: 1.5, pointRadius: 0, fill: false }] },
        options: { ...chartDefaults, scales: { ...chartDefaults.scales, y: { ...chartDefaults.scales.y, title: { display: true, text: 'T (LJ)', color: '#888', font: { size: 10 } } } } }
      });
      new Chart(document.getElementById('adv-chart-pe'), {
        type: 'line', data: { labels: steps, datasets: [{ data: snapshots.map(s => s.potential_energy), borderColor: '#ce93d8', borderWidth: 1.5, pointRadius: 0, fill: false }] },
        options: { ...chartDefaults, scales: { ...chartDefaults.scales, y: { ...chartDefaults.scales.y, title: { display: true, text: 'PE (LJ)', color: '#888', font: { size: 10 } } } } }
      });
    }
    if (rdf.length > 0) {
      new Chart(document.getElementById('adv-chart-rdf'), {
        type: 'line', data: { labels: rdf.map(p => p.r.toFixed(2)), datasets: [{ data: rdf.map(p => p.g_r), borderColor: '#4fc3f7', borderWidth: 1.5, pointRadius: 0, fill: false }] },
        options: { ...chartDefaults, scales: { ...chartDefaults.scales, x: { ...chartDefaults.scales.x, title: { display: true, text: 'r (Å)', color: '#888', font: { size: 10 } } }, y: { ...chartDefaults.scales.y, title: { display: true, text: 'g(r)', color: '#888', font: { size: 10 } } } } }
      });
    }
  }

  function renderCoupledResults(r, container) {
    const md = r.md || {};
    const fem = r.fem || {};

    const statsHtml = `<div style="margin-bottom:1.5rem;">
      <div style="font-size:12px;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:0.75rem;">MD Phase</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:1rem;margin-bottom:1.25rem;">
        ${statCard('Final Temp', md.final_temperature != null ? md.final_temperature.toFixed(4) + ' LJ' : '—', '#ff9800')}
        ${statCard('Diffusion', md.avg_diffusion != null ? md.avg_diffusion.toFixed(5) : '—', '#80cbc4')}
        ${statCard('Atoms', md.n_atoms || '—', '#4fc3f7')}
      </div>
      <div style="font-size:12px;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:0.75rem;">FEM Phase</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:1rem;">
        ${statCard('Converged', fem.converged ? 'Yes' : 'No', fem.converged ? '#4CAF50' : '#f44336')}
        ${statCard('Iterations', fem.iterations || '—', '#ce93d8')}
        ${statCard('Max Field', fem.max_value != null ? fem.max_value.toFixed(1) : '—', '#ff9800')}
        ${statCard('Min Field', fem.min_value != null ? fem.min_value.toFixed(1) : '—', '#4fc3f7')}
        ${statCard('Residual', fem.residual != null ? fem.residual.toExponential(2) : '—', '#a5d6a7')}
        ${statCard('Cycles', r.cycles || '—', '#f48fb1')}
      </div>
    </div>`;

    let chartsHtml = '';
    if ((md.snapshots || []).length > 0) {
      chartsHtml += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;margin-bottom:1.5rem;">
        <div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;">
          <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">MD Temperature</div>
          <canvas id="adv-chart-md-temp" height="160"></canvas>
        </div>
        <div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;">
          <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">FEM Thermal Field (center row)</div>
          <canvas id="adv-chart-fem-row" height="160"></canvas>
        </div>
      </div>`;
    }

    container.innerHTML = statsHtml + chartsHtml;

    if ((md.snapshots || []).length > 0) {
      const snaps = md.snapshots;
      const chartDefaults = { responsive: true, animation: false, plugins: { legend: { display: false } }, scales: {
        x: { ticks: { color: '#888', maxTicksLimit: 6 }, grid: { color: 'rgba(255,255,255,0.05)' } },
        y: { ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.05)' } }
      }};
      new Chart(document.getElementById('adv-chart-md-temp'), {
        type: 'line', data: { labels: snaps.map(s => s.step), datasets: [{ data: snaps.map(s => s.temperature), borderColor: '#ff9800', borderWidth: 1.5, pointRadius: 0, fill: false }] },
        options: chartDefaults
      });
      if (fem.field && fem.field.length > 0) {
        const midRow = fem.field[Math.floor(fem.field.length / 2)];
        new Chart(document.getElementById('adv-chart-fem-row'), {
          type: 'line', data: { labels: midRow.map((_, i) => i), datasets: [{ data: midRow, borderColor: '#ce93d8', borderWidth: 1.5, pointRadius: 0, fill: true, backgroundColor: 'rgba(206,147,216,0.1)' }] },
          options: { ...chartDefaults, scales: { ...chartDefaults.scales, y: { ...chartDefaults.scales.y, title: { display: true, text: 'T (K)', color: '#888', font: { size: 10 } } } } }
        });
      }
    }
  }

  function renderBatchResults(r, container) {
    const runs = r.runs || [];
    if (runs.length === 0) {
      container.innerHTML = `<p style="color:var(--text-dim);">No run data available.</p>`;
      return;
    }

    const tableRows = runs.map((run, i) => {
      const epsilons = Object.entries(run).filter(([k]) => k.includes('epsilon'));
      return `<tr style="border-bottom:1px solid var(--border);">
        <td style="padding:0.5rem 1rem;color:var(--text-dim);font-size:13px;">${i + 1}</td>
        ${epsilons.map(([k, v]) => `<td style="padding:0.5rem 1rem;font-size:13px;font-family:monospace;">${Number(v).toFixed(2)}</td>`).join('')}
        <td style="padding:0.5rem 1rem;font-size:13px;color:#ff9800;">${run.final_temperature != null ? run.final_temperature.toFixed(4) : '—'}</td>
        <td style="padding:0.5rem 1rem;font-size:13px;color:#80cbc4;">${run.avg_diffusion != null ? run.avg_diffusion.toFixed(5) : '—'}</td>
      </tr>`;
    }).join('');

    const epsilonHeaders = Object.keys(runs[0]).filter(k => k.includes('epsilon'))
      .map(k => `<th style="padding:0.5rem 1rem;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-dim);">${k}</th>`).join('');

    const tableHtml = `<div style="overflow-x:auto;margin-bottom:1.5rem;">
      <table style="width:100%;border-collapse:collapse;background:var(--bg-alt);border-radius:6px;overflow:hidden;">
        <thead><tr style="border-bottom:2px solid var(--border);">
          <th style="padding:0.5rem 1rem;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-dim);">#</th>
          ${epsilonHeaders}
          <th style="padding:0.5rem 1rem;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#ff9800;">Final Temp (LJ)</th>
          <th style="padding:0.5rem 1rem;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#80cbc4;">Diffusion</th>
        </tr></thead>
        <tbody>${tableRows}</tbody>
      </table>
    </div>`;

    const chartHtml = `<div style="display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;">
      <div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;">
        <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">Final Temperature by Run</div>
        <canvas id="adv-chart-batch-temp" height="160"></canvas>
      </div>
      <div style="background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;padding:1rem;">
        <div style="font-size:12px;color:var(--text-dim);margin-bottom:0.5rem;text-transform:uppercase;letter-spacing:0.5px;">Diffusion by Run</div>
        <canvas id="adv-chart-batch-diff" height="160"></canvas>
      </div>
    </div>`;

    container.innerHTML = tableHtml + chartHtml;

    const chartDefaults = { responsive: true, animation: false, plugins: { legend: { display: false } }, scales: {
      x: { ticks: { color: '#888', maxTicksLimit: 10 }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.05)' } }
    }};
    const labels = runs.map((_, i) => `Run ${i + 1}`);
    new Chart(document.getElementById('adv-chart-batch-temp'), {
      type: 'bar', data: { labels, datasets: [{ data: runs.map(r => r.final_temperature), backgroundColor: 'rgba(255,152,0,0.5)', borderColor: '#ff9800', borderWidth: 1 }] },
      options: chartDefaults
    });
    new Chart(document.getElementById('adv-chart-batch-diff'), {
      type: 'bar', data: { labels, datasets: [{ data: runs.map(r => r.avg_diffusion), backgroundColor: 'rgba(128,203,196,0.5)', borderColor: '#80cbc4', borderWidth: 1 }] },
      options: chartDefaults
    });
  }

  document.getElementById('adv-jobs-btn').addEventListener('click', () =>
    showJobsModal(null, 'All Adv Module'));

  document.getElementById('adv-jobs-modal-close').addEventListener('click', () => {
    const modal = document.getElementById('adv-jobs-modal');
    modal.classList.add('hidden');
    modal.style.display = 'none';
  });

  document.querySelectorAll('.adv-jobs-tab').forEach(tab => {
    tab.addEventListener('mouseenter', () => {
      if (!tab.classList.contains('active')) {
        tab.style.color = 'var(--text-bright)';
        tab.style.background = 'rgba(255, 255, 255, 0.05)';
      }
    });
    tab.addEventListener('mouseleave', () => {
      if (!tab.classList.contains('active')) {
        tab.style.color = 'var(--text-dim)';
        tab.style.background = 'none';
      }
    });

    tab.addEventListener('click', async () => {
      document.querySelectorAll('.adv-jobs-tab').forEach(t => {
        t.classList.remove('active');
        t.style.color = 'var(--text-dim)';
        t.style.background = 'none';
        t.style.borderBottom = 'none';
      });
      tab.classList.add('active');
      tab.style.color = 'var(--text-bright)';
      tab.style.background = 'rgba(245, 158, 11, 0.1)';
      tab.style.borderBottom = '3px solid #f59e0b';
      currentJobStatus = tab.dataset.status;
      await refreshJobsList();
    });
  });

  initRfCompaction();
  initPhysicsCalc();
}
